import React from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  IconButton,
  useDisclosure,
} from "@chakra-ui/react";
import InputSearch from "@components/Sales/Inputs/InputSearch";
import salesService from "@services/Sales/SalesService";
import Alert from "@components/common/Alert";
import { LoginContext } from "@context/loginContext";
import TableMyOportunity from "@components/Sales/TableMyOportunity";
import InputSelect from "@components/Sales/Inputs/InputSelect";
import { optionFase, optionOpportunityCreator } from "@helpers/optionSelect";
import Spinner from "@components/common/Spinner";
import { formatDatePtBr } from "@helpers/format";
import InputText from "@components/Sales/Inputs/InputText";
import Button from "@components/common/Button";
import { RxUpdate } from "react-icons/rx";
import type { TableRow } from "src/@types/Sales";
import { theme } from "src/styles/theme";
import { OportunityContext } from "@context/oportunityContex";
import useWindowDimensions from "@hook/useWindowDimensions";
import { DEFAULT_PHASES } from "@helpers/defaultValues";
import { FiMaximize2 } from "react-icons/fi";
import { cookieKeys, getCookie } from "@helpers/cookies";
import { MdArrowBack } from "react-icons/md";

const MyOportunityComponent = () => {
  const { setIndexTabDetails } = React.useContext(OportunityContext);

  const handleNavigation = () => {
    setIndexTabDetails(0);
  };

  const MyOportunityComponentOriginalWrapper = ({ navigation }: any) => {
    const idCorretor = getCookie(cookieKeys.idCorretor) || "";
    const { signOut } = React.useContext(LoginContext);
    const { faseMyOportunity: faseFromContext } =
      React.useContext(OportunityContext);

    const [faseMyOportunityLocal, setFaseMyOportunityLocal] = React.useState<
      string[] | string
    >(() => (Array.isArray(faseFromContext) ? faseFromContext : []));
    const [isUnauthorized, setIsUnauthorized] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [dataOportunity, setDataOportunity] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [newSearch, setNewSearch] = React.useState("");
    const [oportunityCreator, setOportunityCreator] = React.useState<
      string | string[]
    >([]);
    const [dateStart, setDateStart] = React.useState("");
    const [dateEnd, setDateEnd] = React.useState("");
    const [filtersLoaded, setFiltersLoaded] = React.useState(false);

    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const { width } = useWindowDimensions();
    const isMobile = width < 768;
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(0);
    const [totalRegistros, setTotalRegistros] = React.useState(0);

    const ITEMS_PER_PAGE = 50;

    const fases = React.useMemo(() => {
      const fromLocal = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : [faseMyOportunityLocal];
      const fromContext = Array.isArray(faseFromContext)
        ? faseFromContext
        : [faseFromContext];
      const isFromContext =
        fromLocal.length === fromContext.length &&
        fromLocal.every((f) => fromContext.includes(f));
      return isFromContext ? fromContext : fromLocal;
    }, [faseMyOportunityLocal, faseFromContext]);

    const faseToDisplay = React.useMemo(() => {
      const local = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : [faseMyOportunityLocal];
      const context = Array.isArray(faseFromContext)
        ? faseFromContext
        : [faseFromContext];
      const isFromContext =
        local.length === context.length &&
        local.every((f) => context.includes(f));
      return isFromContext ? context : local;
    }, [faseMyOportunityLocal, faseFromContext]);

    const handleGetOportunity = React.useCallback(
      async (
        page = 1,
        overrides?: {
          search?: string;
          fases?: string[];
          oportunityCreator?: string | string[];
          dateStart?: string;
          dateEnd?: string;
        },
      ) => {
        setLoading(true);

        const currentSearch = overrides?.search ?? search;
        const currentFases = overrides?.fases ?? fases;
        const currentOpportunityCreator =
          overrides?.oportunityCreator ?? oportunityCreator;
        const currentDateStart = overrides?.dateStart ?? dateStart;
        const currentDateEnd = overrides?.dateEnd ?? dateEnd;

        const handleNumberFase = () => {
          if (
            currentFases.some((f) =>
              f.toLowerCase().includes("oportunidades vencidas"),
            )
          )
            return 1;
          if (
            currentFases.some((f) =>
              f.toLowerCase().includes("aguardando atendimento"),
            )
          )
            return 2;
          if (currentFases.some((f) => f.toLowerCase().includes("contrato")))
            return 4;
          if (
            currentFases.some((f) => f.toLowerCase().includes("cliente ouro"))
          )
            return 5;
          return 3;
        };

        const handleNumberRank = () => {
          if (currentFases.some((f) => f.toLowerCase().includes("bronze")))
            return 1;
          if (currentFases.some((f) => f.toLowerCase().includes("prata")))
            return 2;
          if (currentFases.some((f) => f.toLowerCase().includes("ouro")))
            return 3;
          if (currentFases.some((f) => f.toLowerCase().includes("diamante")))
            return 4;
          if (currentFases.some((f) => f.toLowerCase().includes("aço")))
            return 5;
          if (
            currentFases.some((f) => f.toLowerCase().includes("não elegível"))
          )
            return 6;
          return null;
        };

        const isFilterFase =
          currentFases.some((f) =>
            f.toLowerCase().includes("oportunidades vencidas"),
          ) ||
          currentFases.includes("OP's Cliente Ouro") ||
          currentFases.includes("Contrato");

        const filter = isFilterFase ? handleNumberFase() : undefined;

        try {
          const response = await salesService.getOportunity({
            idCorretorSalesforce: idCorretor,
            DataInicio: currentDateStart,
            DataFim: currentDateEnd,
            filtro: filter,
            rank: handleNumberRank(),
            filtroCriadorOportunidade: currentOpportunityCreator,
            fase: currentFases,
            clienteOuIdDaOportunidade: currentSearch,
            itensPorPagina: ITEMS_PER_PAGE,
            Pagina: page,
          });

          if (response.status === 401) {
            setIsUnauthorized(true);
            setDataOportunity([]);
            setTotalPages(0);
            setTotalRegistros(0);
            setCurrentPage(1);
            setLoading(false);
            return;
          }

          if (response.status === 400) {
            setDataOportunity([]);
            setTotalPages(0);
            setTotalRegistros(0);
            setCurrentPage(1);
            setLoading(false);
            return;
          }

          setDataOportunity(
            Array.isArray(response?.itens) ? response.itens : [],
          );
          setCurrentPage(response?.paginaAtual ?? page);
          setTotalPages(response?.totalPaginas ?? 0);
          setTotalRegistros(response?.totalRegistros ?? 0);
          setNewSearch(search);
        } catch (error) {
          console.log("errorHandleGetSales", error);
        } finally {
          setLoading(false);
        }
      },
      [fases, idCorretor, dateStart, dateEnd, oportunityCreator, search],
    );

    const handleAdjustNameFase = (faseMyOportunity: string | string[]) => {
      if (Array.isArray(faseMyOportunity)) {
        return faseMyOportunity
          .map((fase) =>
            fase.toLowerCase().includes("oportunidades vencidas")
              ? fase.split("-")[0]?.trim()
              : fase,
          )
          .join(", ");
      } else {
        if (faseMyOportunity.toLowerCase().includes("oportunidades vencidas"))
          return faseMyOportunity.split("-")[0]?.trim();
        return faseMyOportunity;
      }
    };

    const handleAdjustName = (value: string | string[]) => {
      if (Array.isArray(value)) return value.join(", ");
      return value;
    };

    // Disparo automático

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      localStorage.setItem("@myOpp_search", value);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.length >= 3 || value.length === 0) {
        // mínimo 3 chars (ou campo limpo)
        debounceRef.current = setTimeout(() => {
          handleGetOportunity(1, { search: value });
        }, 1000); // 1 segundo
      }
    };

    const handleFaseChange = (
      _e: React.ChangeEvent<HTMLInputElement>,
      selected: string[] | string,
    ) => {
      let updatedFases: string[] = [];

      if (typeof selected === "string") {
        updatedFases = [selected];
      } else {
        const hasTodas = selected.includes("Todas");
        const prevHasTodas = faseMyOportunityLocal.includes("Todas");

        if (hasTodas && !prevHasTodas) {
          updatedFases = optionFase.map((option) => option.name);
        } else if (!hasTodas && prevHasTodas) {
          updatedFases = selected.filter((f) => f !== "Todas");
        } else {
          updatedFases = selected.filter((f) => f !== "Todas");
        }
      }

      setCurrentPage(1);
      setFaseMyOportunityLocal(updatedFases);
      localStorage.setItem("@myOpp_fase", JSON.stringify(updatedFases));

      handleGetOportunity(1, {
        fases: updatedFases,
        search,
        oportunityCreator,
        dateStart,
        dateEnd,
      });
    };

    const handleOportunityCreatorChange = (
      _e: React.ChangeEvent<HTMLInputElement>,
      selected: string | string[],
    ) => {
      setCurrentPage(1);
      setOportunityCreator(selected);
      localStorage.setItem(
        "@myOpp_oportunityCreator",
        JSON.stringify(selected),
      );

      handleGetOportunity(1, {
        oportunityCreator: selected,
        search,
        fases,
        dateStart,
        dateEnd,
      });
    };

    const handleDateStartChange = (value: string) => {
      const formatted = formatDatePtBr(value);
      setDateStart(formatted);
      localStorage.setItem("@myOpp_dateStart", formatted);

      if (formatted.length === 10 || formatted.length === 0) {
        handleGetOportunity(1, {
          dateStart: formatted,
          dateEnd,
          search,
          fases,
          oportunityCreator,
        });
      }
    };

    const handleDateEndChange = (value: string) => {
      const formatted = formatDatePtBr(value);
      setDateEnd(formatted);
      localStorage.setItem("@myOpp_dateEnd", formatted);

      if (formatted.length === 10 || formatted.length === 0) {
        handleGetOportunity(1, {
          dateEnd: formatted,
          dateStart,
          search,
          fases,
          oportunityCreator,
        });
      }
    };

    const handleClearFilters = () => {
      const clearedSearch = "";
      const clearedFases = DEFAULT_PHASES;
      const clearedOpportunityCreator: string[] = [];
      const clearedDateStart = "";
      const clearedDateEnd = "";

      localStorage.removeItem("@myOpp_search");
      localStorage.setItem("@myOpp_fase", JSON.stringify(clearedFases));
      localStorage.removeItem("@myOpp_oportunityCreator");
      localStorage.removeItem("@myOpp_dateStart");
      localStorage.removeItem("@myOpp_dateEnd");

      setSearch(clearedSearch);
      setFaseMyOportunityLocal(clearedFases);
      setOportunityCreator(clearedOpportunityCreator);
      setDateStart(clearedDateStart);
      setDateEnd(clearedDateEnd);
      setCurrentPage(1);

      handleGetOportunity(1, {
        search: clearedSearch,
        fases: clearedFases,
        oportunityCreator: clearedOpportunityCreator,
        dateStart: clearedDateStart,
        dateEnd: clearedDateEnd,
      });
    };

    // Dados filtrados / paginados

    const filteredRows = React.useMemo(() => {
      if (!dataOportunity || dataOportunity.length === 0) return [];

      const faseArray = Array.isArray(faseMyOportunityLocal)
        ? faseMyOportunityLocal
        : faseMyOportunityLocal
          ? [faseMyOportunityLocal]
          : [];

      let filteredData = dataOportunity.filter((row: TableRow) => {
        const isRankingFilter = faseArray.some((item) => {
          const itemLower = item?.toLowerCase() || "";
          return (
            itemLower.includes("diamante") ||
            itemLower.includes("ouro") ||
            itemLower.includes("prata") ||
            itemLower.includes("bronze") ||
            itemLower.includes("aço") ||
            itemLower.includes("não elegível") ||
            itemLower.includes("ranking não disponível")
          );
        });

        const isSpecialFilter = faseArray.some((item) => {
          const itemLower = item?.toLowerCase() || "";
          return (
            itemLower.includes("oportunidades vencidas") ||
            itemLower.includes("cliente ouro")
          );
        });

        let isMatchingPhase = true;

        if (!(isRankingFilter || isSpecialFilter)) {
          if (
            !faseMyOportunityLocal ||
            (Array.isArray(faseMyOportunityLocal) &&
              faseMyOportunityLocal.length === 0)
          ) {
            isMatchingPhase = true;
          } else {
            const rowFase = row.fase?.toLowerCase() || "";
            isMatchingPhase = faseArray.some((faseItem) => {
              const lowerFase = faseItem?.toLowerCase() || "";
              if (lowerFase === "" || lowerFase === "todas") return true;
              return rowFase === lowerFase;
            });
          }
        }

        if (!isMatchingPhase) return false;

        return true;
      });

      const uniqueMap = new Map<string, TableRow>();
      filteredData.forEach((row) => {
        if (!uniqueMap.has(row.oportunidadeId))
          uniqueMap.set(row.oportunidadeId, row);
      });

      return Array.from(uniqueMap.values());
    }, [dataOportunity, faseMyOportunityLocal]);

    const paginatedRows = React.useMemo(() => {
      return Array.isArray(filteredRows) ? filteredRows : [];
    }, [filteredRows]);

    React.useEffect(() => {
      if (totalRegistros === 0) {
        if (currentPage !== 1) setCurrentPage(1);
        return;
      }
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
        return;
      }
      if (currentPage < 1) setCurrentPage(1);
    }, [totalRegistros, totalPages, currentPage]);

    React.useEffect(() => {
      const storedSearch = localStorage.getItem("@myOpp_search");
      const storedFase = localStorage.getItem("@myOpp_fase");
      const storedOriginAccount = localStorage.getItem(
        "@myOpp_oportunityCreator",
      );
      const storedDateStart = localStorage.getItem("@myOpp_dateStart");
      const storedDateEnd = localStorage.getItem("@myOpp_dateEnd");

      if (storedSearch) setSearch(storedSearch);
      if (storedOriginAccount)
        setOportunityCreator(JSON.parse(storedOriginAccount));
      if (storedDateStart) setDateStart(storedDateStart);
      if (storedDateEnd) setDateEnd(storedDateEnd);

      const parsedFase: string[] = storedFase
        ? JSON.parse(storedFase)
        : DEFAULT_PHASES;
      const context = Array.isArray(faseFromContext)
        ? faseFromContext
        : [faseFromContext];

      if (context.length === 0) {
        setFaseMyOportunityLocal((prev) => {
          if (!Array.isArray(prev)) return parsedFase;
          const isSame =
            prev.length === parsedFase.length &&
            prev.every((val, idx) => val === parsedFase[idx]);
          return isSame ? prev : parsedFase;
        });
      }

      setFiltersLoaded(true);
    }, [faseFromContext, faseMyOportunityLocal]);

    React.useEffect(() => {
      if (filtersLoaded && idCorretor) {
        handleGetOportunity(1);
      }
    }, [filtersLoaded, idCorretor]);

    return (
      <Flex
        direction="column"
        h={{ base: "full", sm: "full", md: "92vh" }}
        overflow="hidden"
      >
        <Alert
          onOpen={isUnauthorized}
          title="Sessão expirada"
          titleButton={"Fazer login novamente"}
          description={
            "Sua sessão expirou, faça login novamente para ter acesso."
          }
          onClick={() => {
            signOut();
          }}
        />

        {/* Header */}
        <Flex
          direction={{ base: "column", md: "row" }}
          w="99%"
          h={{ base: undefined, md: "80px" }}
          alignItems="center"
          justifyContent="space-between"
          gap={{ base: 4, md: 0 }}
          mt={{ base: "20px", md: "0px" }}
          px={{ base: 2, md: 0 }}
        >
          <Box
            display="flex"
            flexDir="column"
            alignItems={isMobile ? "flex-start" : "center"}
          >
            <Text fontSize={28} fontWeight="semibold" color={theme.text}>
              Minhas oportunidades
            </Text>
            <Text fontSize={14} color={theme.text50}>
              Selecione uma oportunidade para ver mais detalhes, criar
              agendamentos e mais
            </Text>
          </Box>

          {/* Apenas Limpar Filtros e Atualizar */}
          <Flex
            gap={"6px"}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "full", md: 210 }}
          >
            <Button title="Limpar Filtros" onClick={handleClearFilters} />
            <Button
              title={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap="4px"
                >
                  <Icon fontSize="16px" color="white" as={RxUpdate} />
                  Atualizar
                </Box>
              }
              onClick={() => handleGetOportunity()}
            />
          </Flex>
        </Flex>

        {/* Filtros */}
        <Flex
          flexDir="column"
          w="99%"
          h={isMobile ? "auto" : "120px"}
          bg="white"
          borderRadius="8px"
          mt="8px"
          gap="6px"
          p="10px"
          alignItems="center"
          justifyContent="center"
        >
          <Flex>
            <Text fontWeight="semibold">Pesquisa e filtros</Text>
          </Flex>

          <Flex
            gap="6px"
            flexWrap={isMobile ? "wrap" : "nowrap"}
            flexDirection={isMobile ? "column" : "row"}
            width="100%"
          >
            <Box
              display="flex"
              width={isMobile ? "100%" : "80%"}
              flexDirection={isMobile ? "column" : "row"}
              alignItems={isMobile ? "baseline" : "center"}
            >
              <InputSearch
                isOportunity
                label="Nome do cliente ou ID da oportunidade"
                placeholder={`Pesquisar...`}
                value={search}
                onChange={handleSearchChange}
              />
            </Box>

            <Flex
              width={isMobile ? "100%" : "95%"}
              gap="6px"
              flexDirection={isMobile ? "column" : "row"}
            >
              <InputSelect
                isMulti
                widht="17%"
                widhtMobile="90%"
                title="Fase"
                label={
                  faseToDisplay.length !== 0
                    ? handleAdjustNameFase(faseToDisplay)
                    : "Selecione uma opção"
                }
                isSelected={faseMyOportunityLocal}
                options={optionFase}
                selectedOption={faseMyOportunityLocal}
                onChange={handleFaseChange}
              />

              <InputSelect
                isMulti
                widht="17%"
                widhtMobile="90%"
                title="Criador da oportunidade"
                label={
                  oportunityCreator.length !== 0
                    ? handleAdjustName(oportunityCreator)
                    : "Selecione uma opção"
                }
                isSelected={oportunityCreator}
                options={optionOpportunityCreator}
                selectedOption={oportunityCreator}
                onChange={handleOportunityCreatorChange}
              />
            </Flex>

            <Flex flexDirection="column" width={isMobile ? "100%" : "80%"}>
              <Text
                fontSize="12px"
                color={theme.text}
                marginLeft="5px"
                height="18px"
              >
                Filtrar por data de criação
              </Text>
              <Flex gap="6px" flexDirection={isMobile ? "column" : "row"}>
                <InputText
                  label=""
                  name="dateStart"
                  placeholder={`De __/__/___`}
                  value={dateStart}
                  onChange={(e) => handleDateStartChange(e.target.value)}
                  maxLength={10}
                  iconSee={false}
                />
                <InputText
                  label=""
                  name="dateEnd"
                  placeholder={`Até __/__/___`}
                  value={dateEnd}
                  onChange={(e) => handleDateEndChange(e.target.value)}
                  maxLength={10}
                  iconSee={false}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* TABELA/CARDS DE OPORTUNIDADES */}
        <Box
          mt={4}
          bg="white"
          borderRadius="8px"
          w="full"
          flex="1"
          display="flex"
          flexDirection="column"
          overflow="hidden"
          shadow="sm"
        >
          <Flex justify="flex-end" mb={"-30px"}>
            <IconButton
              zIndex={9999}
              w={"fit-content"}
              aria-label="Ver tabela em tela cheia"
              icon={<FiMaximize2 />}
              size="sm"
              variant="unstyled"
              onClick={onOpen}
            />
          </Flex>

          {loading ? (
            <Flex
              w="100%"
              h={{ base: "400px", xl: "330px", "2xl": "620px" }}
              justify="center"
              align="center"
            >
              <Spinner />
            </Flex>
          ) : (
            <>
              <Box
                w="full"
                flex="1"
                overflow="auto"
                css={{
                  "&::-webkit-scrollbar": { width: "5px", height: "5px" },
                  "&::-webkit-scrollbar-track": { background: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    background: theme.primary,
                    borderRadius: "3px",
                  },
                }}
              >
                {!loading && filteredRows.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    width="98%"
                    border="1px solid"
                    borderColor={theme.disable100}
                    padding="5px"
                    margin="5px"
                    sx={{
                      "@media (min-width: 1200px)": {
                        padding: "8px",
                        width: "99%",
                      },
                      "@media (min-width: 768px)": { width: "70%" },
                      "@media (max-width: 425px)": { width: "90%" },
                    }}
                  >
                    {!search &&
                    (!Array.isArray(faseMyOportunityLocal) ||
                      faseMyOportunityLocal.length === 0) &&
                    (!Array.isArray(oportunityCreator) ||
                      oportunityCreator.length === 0) &&
                    !dateStart &&
                    !dateEnd ? (
                      <Stack spacing={2}>
                        <Text
                          fontSize={{ base: "sm", md: "md" }}
                          fontWeight="bold"
                          color={theme.text}
                        >
                          Nenhuma oportunidade encontrada.
                        </Text>
                        <Text fontSize="xs" color={theme.text}>
                          Ajuste os filtros para carregar as oportunidades.
                        </Text>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color={theme.error}
                        >
                          Nenhuma oportunidade corresponde aos filtros
                          selecionados.
                        </Text>
                        <Text fontSize="xs" color={theme.text}>
                          O backend retornou oportunidades, porém após a
                          aplicação dos filtros no frontend não restou nenhum
                          resultado para exibição.
                        </Text>
                        <Text fontSize="xs" color={theme.text}>
                          Isso pode acontecer quando a API retorna registros com
                          fases diferentes das fases atualmente selecionadas.
                        </Text>
                        <Text fontSize="xs" color={theme.text}>
                          Se desejar visualizar esses registros, revise os
                          filtros.
                        </Text>
                      </Stack>
                    )}
                  </Box>
                ) : (
                  <>
                    <Modal
                      isOpen={isOpen}
                      onClose={onClose}
                      size="full"
                      scrollBehavior="inside"
                    >
                      <ModalOverlay />
                      <ModalContent
                        maxW="95vw"
                        maxH="95vh"
                        h="95vh"
                        borderRadius="12px"
                      >
                        <ModalHeader>Minhas Oportunidades</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody p={4} display="flex" flexDirection="column">
                          <Box flex="1" overflow="auto">
                            <TableMyOportunity
                              data={paginatedRows}
                              onClick={navigation}
                            />
                          </Box>
                        </ModalBody>
                      </ModalContent>
                    </Modal>

                    {!isOpen && (
                      <TableMyOportunity
                        data={paginatedRows}
                        onClick={navigation}
                      />
                    )}
                  </>
                )}
              </Box>

              {totalPages > 1 && (
                <Flex
                  justify="flex-start"
                  align="center"
                  py={3}
                  px={4}
                  gap={2}
                  borderTop="1px solid"
                  borderColor={theme.disable100}
                  bg="white"
                  borderRadius="0 0 8px 8px"
                >
                  <IconButton
                    aria-label="Página anterior"
                    icon={<MdArrowBack />}
                    size="sm"
                    variant="ghost"
                    isDisabled={currentPage === 1}
                    onClick={() => {
                      if (currentPage > 1) handleGetOportunity(currentPage - 1);
                    }}
                    color={currentPage === 1 ? theme.disable : theme.primary}
                    borderColor={
                      currentPage === 1 ? theme.disable : theme.primary
                    }
                    w={"fit-content"}
                  />

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      if (totalPages <= 7) return true;
                      if (page === 1 || page === totalPages) return true;
                      if (Math.abs(page - currentPage) <= 2) return true;
                      return false;
                    })
                    .reduce<(number | "...")[]>((acc, page, idx, arr) => {
                      if (
                        idx > 0 &&
                        (page as number) - (arr[idx - 1] as number) > 1
                      )
                        acc.push("...");
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "..." ? (
                        <Text
                          key={`ellipsis-${idx}`}
                          fontSize="13px"
                          color={theme.text50}
                          px={1}
                          w={"fit-content"}
                        >
                          ...
                        </Text>
                      ) : (
                        <Box
                          key={item}
                          as="button"
                          px={3}
                          py={1}
                          w={"fit-content"}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={
                            currentPage === item
                              ? theme.primary
                              : theme.disable100
                          }
                          bg={currentPage === item ? theme.primary : "white"}
                          color={currentPage === item ? "white" : theme.text}
                          cursor="pointer"
                          fontSize="13px"
                          fontWeight="medium"
                          onClick={() => {
                            handleGetOportunity(item as number);
                          }}
                          _hover={{ opacity: 0.8 }}
                        >
                          {item}
                        </Box>
                      ),
                    )}

                  <IconButton
                    aria-label="Próxima página"
                    icon={
                      <MdArrowBack style={{ transform: "rotate(180deg)" }} />
                    }
                    size="sm"
                    variant="ghost"
                    isDisabled={currentPage === totalPages}
                    onClick={() => {
                      if (currentPage < totalPages)
                        handleGetOportunity(currentPage + 1);
                    }}
                    color={
                      currentPage === totalPages ? theme.disable : theme.primary
                    }
                    borderColor={
                      currentPage === totalPages ? theme.disable : theme.primary
                    }
                    w={"fit-content"}
                  />
                </Flex>
              )}
            </>
          )}
        </Box>
      </Flex>
    );
  };

  return <MyOportunityComponentOriginalWrapper navigation={handleNavigation} />;
};

export default React.memo(MyOportunityComponent);
